import { mediaUrl, useSiteMedia } from "../lib/siteMedia";
import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import SectionHeader from "../components/SectionHeader";

const COCKTAILS = [
  { name: "The Founder", desc: "Yamazaki 18, smoked maple, walnut bitters, charred oak", price: 38 },
  { name: "Cigar & Cognac", desc: "Hennessy Paradis, fig demi, Cuban cedar smoke", price: 42 },
  { name: "Lake Nona Sour", desc: "Pappy 15, Florida honey, lemon, egg white", price: 36 },
  { name: "Old World Old Fashioned", desc: "Michter's 25 toasted barrel finish, demerara, orange oil", price: 48 },
  { name: "Champagne 1937", desc: "Krug Grande Cuvée, Chartreuse VEP, lemon twist", price: 44 },
  { name: "Velvet Negroni", desc: "Empress 1908, Carpano Antica, Campari, burnt rosemary", price: 28 },
];

const WHISKEY = [
  ["Yamazaki 25", "Japan", "$240 / oz"],
  ["Pappy Van Winkle 23", "Kentucky", "$320 / oz"],
  ["Macallan M Decanter", "Speyside", "$1,800 / oz"],
  ["Hibiki 30", "Japan", "$420 / oz"],
  ["Hakushu 18", "Japan", "$95 / oz"],
  ["Glenfiddich 30", "Speyside", "$110 / oz"],
];

export default function Bar() {
  const media = useSiteMedia();
  return (
    <div data-testid="bar-page">
      <section className="relative pt-32 min-h-[80vh] flex items-end px-6 sm:px-10 lg:px-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={mediaUrl(media, "bar_hero")} alt="" className="w-full h-full object-cover ken-burns" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-[1600px] mx-auto pb-20 w-full">
          <p className="eyebrow">The Bar</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl mt-6 leading-[1.02] tracking-tight max-w-4xl">
            Rare whiskey. <span className="italic" style={{ color: "var(--twr-bronze)" }}>Quiet rooms.</span>
          </h1>
          <p className="text-[var(--twr-warm-white)]/80 text-lg mt-6 max-w-2xl">
            Two hundred labels. A walk-in private library. Cocktails built by hand, slowly, the old way.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/reservations?type=bar" data-testid="bar-reserve" className="btn-bronze">Reserve at the Bar <ChevronRight size={14} /></Link>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeader eyebrow="Signature Cocktails" title="House classics — built for the long sit." />
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-8 mt-14">
            {COCKTAILS.map((c) => (
              <div key={c.name} className="flex items-baseline gap-4 py-5 border-b border-[var(--twr-line)]">
                <div className="flex-1">
                  <p className="font-serif text-xl">{c.name}</p>
                  <p className="text-[var(--twr-warm-white)]/60 text-sm mt-1.5 leading-relaxed">{c.desc}</p>
                </div>
                <span className="font-serif text-xl text-[var(--twr-bronze)]">${c.price}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-[#0c0b0a]">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeader eyebrow="The Whiskey Library" title="Rare. Reserved. Poured one ounce at a time." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-14">
            {WHISKEY.map(([n, o, p]) => (
              <div key={n} className="surface p-6">
                <p className="font-serif text-xl">{n}</p>
                <p className="text-[var(--twr-warm-white)]/55 text-[12px] uppercase tracking-[0.22em] mt-2">{o}</p>
                <p className="text-[var(--twr-bronze)] mt-4 font-serif">{p}</p>
              </div>
            ))}
          </div>
          <p className="text-[var(--twr-warm-white)]/60 text-sm mt-10">
            Members may request private tastings, vertical flights, and bottle-and-keep service. Speak with the bar director.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 frame aspect-[16/10]">
            <img src={mediaUrl(media, "bar_feature")} alt="Cocktail" className="w-full h-full object-cover" />
          </div>
          <div className="lg:col-span-5">
            <p className="eyebrow">The Hour</p>
            <h2 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">Twilight in the lounge.</h2>
            <p className="text-[var(--twr-warm-white)]/70 mt-6 leading-relaxed">
              Five to seven, weekdays. A short curated list of cocktails, a glass of champagne, and a small plate from the kitchen — for the in-between of work and evening.
            </p>
            <Link to="/reservations?type=bar" className="btn-bronze mt-8">Reserve <ChevronRight size={14} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
